import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

@Component({
  selector: 'app-status-badge',
  standalone: true,
  imports: [
    CommonModule,
  ],
  templateUrl: './status-badge.component.html',
  styleUrl: './status-badge.component.scss',
})
export class StatusBadgeComponent implements ICellRendererAngularComp, OnInit {
  params: any;
  value = '';
  badgeClass = '';
  dotColor = '';
  showDot = false;
  toneStyle: Record<string, string> = {};

  @Input() variant = '';
  private statusCode = '';

  @Input()
  set label(value: string) {
    this.value = value || '';
    if (this.variant === 'my-jobs') {
      this.applyMyJobs(this.statusCode, this.value);
    }
  }

  @Input()
  set status(value: string) {
    this.statusCode = value || '';
    this.apply(this.statusCode, this.value);
  }

  ngOnInit(): void {
    if (this.variant === 'my-jobs') {
      this.applyMyJobs(this.statusCode, this.value);
    }
  }

  agInit(params: any): void {
    this.applyFromParams(params);
  }

  refresh(params: ICellRendererParams<any, any, any>): boolean {
    this.applyFromParams(params);
    return true;
  }

  private applyFromParams(params: any): void {
    this.params = params;
    const rendererParams = params.colDef?.cellRendererParams;
    const extra = rendererParams && typeof rendererParams === 'object' ? rendererParams : {};
    const raw = params.value;

    // Variant object value: { label, variant } (e.g. Data Fidelity badges).
    if (raw && typeof raw === 'object' && 'variant' in raw) {
      this.variant = raw.variant;
      this.value = raw.label ?? '';
      this.badgeClass = this.classForVariant(raw.variant);
      this.showDot = true;
      this.dotColor = '';
      this.toneStyle = {};
      return;
    }

    const title = params.label || extra.label || params.data?.status_title || params.valueFormatted || params.value || '';
    const statusCode = params.status || extra.status || params.data?.status || params.data?.booking_status || params.value;
    this.variant = params.variant || params.badgeContext || extra.variant || this.variant;
    if (this.variant === 'my-jobs') {
      this.applyMyJobs(statusCode, title);
      return;
    }
    this.showDot = false;
    this.toneStyle = {};
    this.value = title;
    this.badgeClass = this.defaultBadgeClass(title);
  }

  private apply(statusCode: string, title?: string): void {
    if (this.variant === 'my-jobs') {
      this.applyMyJobs(statusCode, title || statusCode);
    }
  }

  private classForVariant(variant: string): string {
    switch (variant) {
      case 'factual':
        return 'ag-grid-pill-success';
      case 'hybrid':
        return 'ag-grid-pill-warning';
      case 'modeled':
        return 'ag-grid-pill-secondary';
      default:
        return 'ag-grid-pill-secondary';
    }
  }

  private defaultBadgeClass(value: string): string {
    switch (value) {
      case 'Booked':
      case 'booked':
        return 'ag-grid-pill-info';
      case 'Delivered':
      case 'customer_delivered':
        return 'ag-grid-pill-warning';
      case 'Provider Delivered':
      case 'provider_delivered':
        return 'ag-grid-pill-success';
      case 'In Transit':
      case 'provider_collected':
        return 'ag-grid-pill-success';
      default:
        return 'ag-grid-pill-secondary';
    }
  }

  private applyMyJobs(statusCode: string, title: string): void {
    const fromCode = myJobsStatusTone(statusCode);
    const tone = fromCode.className === 'my-jobs-status-neutral' && title ? myJobsStatusTone(title) : fromCode;
    this.value = title || statusCode;
    this.showDot = true;
    this.dotColor = tone.dot;
    this.badgeClass = `my-jobs-status-badge ${tone.className}`;
    // Inline so AG Grid / Bootstrap cannot leave the pill on the default gray.
    this.toneStyle = {
      background: tone.background,
      color: tone.color,
      border: tone.border,
    };
  }
}

export function myJobsStatusTone(statusCode: string): {
  className: string;
  dot: string;
  background: string;
  color: string;
  border: string;
} {
  const key = (statusCode || '').toLowerCase().replace(/[\s-]+/g, '_');
  if (key === 'active') {
    return { className: 'my-jobs-status-active', dot: '#16a34a', background: '#ebf3ce', color: '#16a34a', border: '1px solid #9ec407' };
  }
  if (key === 'booked' || key === 'in_transit' || key === 'provider_collected' || key === 'customer_collected') {
    return { className: 'my-jobs-status-progress', dot: '#9ec407', background: '#ebf3ce', color: '#6b7280', border: '1px solid #9ec407' };
  }
  if (key === 'complete' || key === 'completed' || key === 'delivered' || key === 'customer_delivered' || key === 'provider_delivered') {
    return { className: 'my-jobs-status-success', dot: '#0f5132', background: '#d1e7dd', color: '#0f5132', border: '1px solid transparent' };
  }
  if (key === 'cancelled' || key === 'canceled' || key === 'expired') {
    return { className: 'my-jobs-status-danger', dot: '#842029', background: '#fae5e5', color: '#842029', border: '1px solid transparent' };
  }
  return { className: 'my-jobs-status-neutral', dot: '#6b7280', background: '#f3f4f6', color: '#374151', border: '1px solid transparent' };
}
